import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';

const CONFIDENCE_COLOURS = {
  high: { bg: '#e6f7ed', fg: '#1b7a45' },
  medium: { bg: '#fff7db', fg: '#8a6d00' },
  low: { bg: '#fdecea', fg: '#a4302a' },
};

/** Filters arrive as arrays, numbers or strings; render each readably. */
function displayValue(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

function FilterReview({
  plannerFilters,
  filterLabels,
  rationale,
  assumptions,
  clarifyingQuestions,
  confidence,
  selectedKeys,
  onToggleKey,
  onApply,
}) {
  const keys = Object.keys(plannerFilters || {});

  if (keys.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        The brief did not give enough to set any filter. Add the audience, platform or budget and
        try again.
      </Alert>
    );
  }

  const reasonFor = (key) =>
    (rationale || []).find((entry) => entry.field === key || filterLabels[key] === entry.field);

  const confidenceStyle = CONFIDENCE_COLOURS[confidence] || CONFIDENCE_COLOURS.medium;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Proposed filters</Typography>
        <Chip
          size="small"
          label={`${confidence} confidence`}
          sx={{ bgcolor: confidenceStyle.bg, color: confidenceStyle.fg, fontWeight: 600 }}
        />
      </Stack>

      <List dense disablePadding sx={{ mt: 1 }}>
        {keys.map((key) => {
          const reason = reasonFor(key);
          return (
            <ListItem key={key} disableGutters alignItems="flex-start" sx={{ py: 0.75 }}>
              <Checkbox
                size="small"
                checked={selectedKeys.includes(key)}
                onChange={() => onToggleKey(key)}
                sx={{ mt: -0.5, mr: 0.5 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {filterLabels[key] || key}: {displayValue(plannerFilters[key])}
                </Typography>
                {reason && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {reason.reason}{' '}
                    {reason.quote ? (
                      <em>&ldquo;{reason.quote}&rdquo;</em>
                    ) : (
                      <em>(inferred &mdash; not stated in the brief)</em>
                    )}
                  </Typography>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>

      {assumptions && assumptions.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Filled in, not stated:
          </Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {assumptions.map((item) => (
              <li key={item}>
                <Typography variant="caption">{item}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {clarifyingQuestions && clarifyingQuestions.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Answering these would sharpen the search:
          </Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {clarifyingQuestions.map((question) => (
              <li key={question}>
                <Typography variant="caption" color="text.secondary">
                  {question}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Button variant="contained" onClick={onApply} disabled={selectedKeys.length === 0} fullWidth>
        {selectedKeys.length === 0
          ? 'Select at least one filter'
          : `Apply ${selectedKeys.length} filter${selectedKeys.length === 1 ? '' : 's'} and search`}
      </Button>
    </Box>
  );
}

export default FilterReview;
